import React, {
    Children,
    cloneElement,
    createContext,
    useContext,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { Check } from 'lucide-react';
import {
    FloatingFocusManager,
    FloatingList,
    FloatingPortal,
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListItem,
    useListNavigation,
    useMergeRefs,
    useRole,
} from '@floating-ui/react';
import { controlResetClassName, cx } from './classNames';
import { floatingPaperClassName, floatingRootFor } from './Popover';
import useOpenState from './useOpenState';

const MenuContext = createContext(null);

const ITEM_ROLES = Object.freeze({
    item: 'menuitem',
    checkbox: 'menuitemcheckbox',
    radio: 'menuitemradio',
});

// MUI MenuItem in the app theme: 34px tall (30px compact), 6px radius,
// 11.5px secondary ink, row-hover background on hover and keyboard focus,
// blue tint when it is the chosen radio item.
const ITEM_CLASS_NAME = cx(
    controlResetClassName,
    'flex w-full min-h-[34px] cursor-pointer items-center gap-2 rounded-md border-0 px-3 py-1.5',
    'text-left text-ui-ink-soft hover:bg-ui-row-hover focus:bg-ui-row-hover focus:outline-none',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ui-focus',
    'disabled:cursor-default disabled:bg-transparent disabled:text-ui-disabled-text',
    '[[data-compact]_&]:min-h-[30px]',
);

/**
 * A menu opened by clicking `trigger` (one element that takes a ref; it is
 * also the menu's accessible name, so give an icon button an aria-label).
 * `children` are MenuItems. Arrow keys, Home and End move between items
 * (wrapping), the first item takes focus on open, and Escape, an outside
 * click or tabbing out closes the menu and returns focus to the trigger.
 * Inside a dialog it renders in that dialog (see Popover floatingRootFor).
 *
 * Open state is kept here unless `open` is given; `onOpenChange(open,
 * event, reason)` reports every request either way.
 *
 * Instead of a trigger, `anchor` places the menu against an element rendered
 * elsewhere (a column header's menu button): the caller opens it through
 * `open`, wires the anchor's aria-expanded / aria-controls to the menu's
 * `id`, and the anchor's own id names the menu. `returnFocus={false}`
 * leaves focus where the caller puts it on close. `autoFocusItem={false}`
 * focuses the menu itself on open, so ArrowDown reaches the first item (as
 * MUI's MenuList does); `offset` is the gap to the anchor in px.
 */
export default function Menu({
    trigger = null,
    anchor = null,
    open: openProp,
    onOpenChange,
    placement = 'bottom-start',
    offset: gap = 4,
    returnFocus = true,
    autoFocusItem = true,
    id,
    className,
    children,
}) {
    const [open, setOpen] = useOpenState(openProp, onOpenChange);
    const [activeIndex, setActiveIndex] = useState(null);
    const elementsRef = useRef([]);
    const {
        refs,
        elements,
        floatingStyles,
        context,
    } = useFloating({
        open,
        onOpenChange: setOpen,
        placement,
        strategy: 'fixed',
        elements: anchor ? { reference: anchor } : undefined,
        whileElementsMounted: autoUpdate,
        middleware: [offset(gap), flip(), shift({ padding: 8 })],
    });
    const click = useClick(context, { enabled: Boolean(trigger) });
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const listNavigation = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        loop: true,
        focusItemOnOpen: autoFocusItem,
    });
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        click,
        dismiss,
        role,
        listNavigation,
    ]);
    const anchorElement = trigger ? Children.only(trigger) : null;
    const anchorRef = useMergeRefs([refs.setReference, anchorElement?.ref]);
    const menuContext = {
        activeIndex,
        getItemProps,
        close: () => setOpen(false),
    };
    return (
        <>
            {anchorElement && cloneElement(
                anchorElement,
                getReferenceProps({ ...anchorElement.props, ref: anchorRef }),
            )}
            {open && (
                <FloatingPortal root={floatingRootFor(elements.domReference)}>
                    <FloatingFocusManager
                        context={context}
                        modal={false}
                        initialFocus={autoFocusItem ? -1 : 0}
                        returnFocus={returnFocus}
                    >
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className={cx(floatingPaperClassName, 'min-w-[160px] p-1 text-[11.5px] leading-[1.3]', className)}
                            {...getFloatingProps(id ? { id } : undefined)}
                        >
                            <MenuContext.Provider value={menuContext}>
                                <FloatingList elementsRef={elementsRef}>{children}</FloatingList>
                            </MenuContext.Provider>
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </>
    );
}

Menu.propTypes = {
    trigger: PropTypes.element,
    anchor: PropTypes.object,
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    placement: PropTypes.string,
    offset: PropTypes.number,
    returnFocus: PropTypes.bool,
    autoFocusItem: PropTypes.bool,
    id: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
};

/**
 * One menu entry. `type` 'item' (role menuitem) runs `onSelect` and closes
 * the menu; 'checkbox' (menuitemcheckbox) and 'radio' (menuitemradio) show a
 * check mark when `checked`. Checkbox items keep the menu open so several
 * can be toggled. `icon` goes in a 36px slot before the label (MUI's
 * ListItemIcon); `icon={null}` keeps the slot empty so labels stay aligned
 * with their neighbours'.
 */
export function MenuItem({
    type = 'item',
    checked = false,
    disabled = false,
    onSelect,
    icon,
    className,
    children,
}) {
    const menu = useContext(MenuContext);
    const { ref, index } = useListItem();
    const checkable = type !== 'item';
    const closes = type !== 'checkbox';
    return (
        <button
            ref={ref}
            type="button"
            role={ITEM_ROLES[type]}
            aria-checked={checkable ? checked : undefined}
            disabled={disabled}
            tabIndex={menu.activeIndex === index ? 0 : -1}
            className={cx(ITEM_CLASS_NAME, type === 'radio' && checked ? 'bg-ui-selected' : 'bg-transparent', className)}
            {...menu.getItemProps({
                onClick: () => {
                    onSelect?.();
                    if (closes) menu.close();
                },
            })}
        >
            {checkable && (
                <span className="inline-flex w-4 shrink-0 justify-center" aria-hidden="true">
                    {checked && <Check size={14} strokeWidth={2.5} />}
                </span>
            )}
            {icon !== undefined && (
                <span className="inline-flex min-w-9 shrink-0 items-center" aria-hidden="true">{icon}</span>
            )}
            <span className="min-w-0 flex-1 truncate">{children}</span>
        </button>
    );
}

MenuItem.propTypes = {
    type: PropTypes.oneOf(['item', 'checkbox', 'radio']),
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    onSelect: PropTypes.func,
    icon: PropTypes.node,
    className: PropTypes.string,
    children: PropTypes.node,
};

/** A line between groups of menu items (MUI's Divider in a menu). */
export function MenuDivider() {
    return <div role="separator" className="my-2 h-0 border-0 border-b border-solid border-hairline" />;
}
